import React, { useEffect, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import {
    ReactFlow,
    Background,
    Controls,
    Node,
    Edge,
    applyNodeChanges,
    NodeChange,
    DefaultEdgeOptions,
    NodeTypes, Position,
} from '@xyflow/react';
import { Note } from "../types/note.ts";
import TurboNode, { TurboNodeData } from "./ui/turbo/TurboNode.tsx";
import { FaFileAlt } from "react-icons/fa";
import { FaExternalLinkAlt } from "react-icons/fa";
import '@xyflow/react/dist/style.css';
import './ui/turbo/turbo.css';

const nodeTypes: NodeTypes = {
    turbo: TurboNode,
};

const defaultEdgeOptions: DefaultEdgeOptions = {
    type: 'smoothstep',
    markerEnd: 'edge-circle',
};

export const RelatedNotes: React.FC<{ note: Note }> = ({ note }) => {
    const [nodes, setNodes] = useState<Node<TurboNodeData>[]>([]);
    const [edges, setEdges] = useState<Edge[]>([]);


    const createRelationships = () => {
        if (note.related_notes) {
            const nodes: Node<TurboNodeData>[] = [];
            const edges: Edge[] = [];
            const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };

            const mainNote = `mainNode-${note.id}`;

            nodes.push({
                id: mainNote,
                data: {
                    label: note.name,
                    icon: <FaFileAlt />,
                    handlePositions: {
                        target: Position.Top,
                        source: Position.Bottom
                    },
                },
                position: center,
                type: 'turbo',
                className: 'light font-bold',
            });

            for (let i = 0; i < note.related_notes.length; i++) {
                const degrees = i * (360 / note.related_notes.length);
                const radians = degrees * (Math.PI / 180);
                const x = 300 * Math.cos(radians) + center.x;
                const y = 300 * Math.sin(radians) + center.y;

                const angleToCenter = Math.atan2(center.y - y, center.x - x) * 180 / Math.PI;
                const normalizedAngle = ((angleToCenter + 180) % 360 + 360) % 360;

                nodes.push({
                    id: `node-${note.related_notes[i].id}`,
                    data: {
                        label: (
                            <Link to={`/notes/${note.related_notes[i].id}`} target="_blank">
                                {note.related_notes[i].name}
                                <FaExternalLinkAlt/>
                            </Link>
                        ),
                        icon: null,
                        handlePositions: {
                            source: { angle: normalizedAngle },
                            target: { angle: (normalizedAngle + 180) % 360 }
                        }
                    },
                    position: { x, y },
                    type: 'turbo',
                    className: 'light',
                });

                edges.push({
                    id: `edge-${note.related_notes[i].id}`,
                    target: mainNote,
                    source: `node-${note.related_notes[i].id}`,
                });

            }

            setNodes(nodes);
            setEdges(edges);
        }
    };

    useEffect(() => {
        createRelationships();
    }, [note]);


    const onNodesChange = useCallback(
        (changes: NodeChange[]) =>
            setNodes((nds) => applyNodeChanges(changes, nds) as Node<TurboNodeData>[]),
        []
    );

    return (
        <div style={{ height: '400px', width: '100%' }}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                fitView
                nodeTypes={nodeTypes}
                defaultEdgeOptions={defaultEdgeOptions}
                onNodesChange={onNodesChange}
                onBeforeDelete={async () => false}
                className="turbo-flow"
            >
                <Background />
                <Controls />
                <svg>
                    <defs>
                        <linearGradient id="edge-gradient">
                            <stop offset="0%" stopColor="#ae53ba" />
                            <stop offset="100%" stopColor="#2a8af6" />
                        </linearGradient>

                        <marker
                            id="edge-circle"
                            viewBox="-5 -5 10 10"
                            refX="0"
                            refY="0"
                            markerUnits="strokeWidth"
                            markerWidth="10"
                            markerHeight="10"
                            orient="auto"
                        >
                            <circle stroke="#2a8af6" strokeOpacity="0.75" r="2" cx="0" cy="0" />
                        </marker>
                    </defs>
                </svg>
            </ReactFlow>
        </div>
    );
};
